'use client'

import { ClientsTebutoAPI, PublicTebutoAPI, TherapistsTebutoAPI } from '@tebuto/api/client-side'
import { ApiError as ClientsApiError } from '@tebuto/api/clients'
import { EventCategory } from '@tebuto/api/clients/models/EventCategory'
import { MinimalWhoAmITherapist } from '@tebuto/api/clients/models/MinimalWhoAmITherapist'
import { WhoAmIClient } from '@tebuto/api/clients/models/WhoAmIClient'
import { ApiError as PublicApiError } from '@tebuto/api/public'
import { ApiError as TherapistsApiError } from '@tebuto/api/therapists'
import { Contract } from '@tebuto/api/therapists/models/Contract'
import { EventRule } from '@tebuto/api/therapists/models/EventRule'
import { WhoAmI } from '@tebuto/api/therapists/models/WhoAmI'
import { WhoAmITherapist } from '@tebuto/api/therapists/models/WhoAmITherapist'
import { WhoAmITherapistUserLinking } from '@tebuto/api/therapists/models/WhoAmITherapistUserLinking'
import { CurrentUserContext } from '@tebuto/guards/CurrentUser'
import { CurrentClientContext } from '@tebuto/guards/current-client-context'
import { CurrentLoginStatusContext } from '@tebuto/guards/LoginStatus'
import { SelectedTherapistIdContext } from '@tebuto/guards/TherapistSelection'
import { MutableRefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { io, Socket } from 'socket.io-client'
import useSWR, { SWRResponse } from 'swr'
import { BareFetcher, FetcherResponse, Key, SWRConfiguration, useSWRConfig } from 'swr/_internal'
import useSWRMutation, { MutationFetcher, SWRMutationConfiguration, SWRMutationResponse } from 'swr/mutation'
import { ERROR_MESSAGES, resolveErrorMessage } from './api'

export function useCurrentClient(): WhoAmIClient {
    const whoAmI = useContext(CurrentClientContext)
    if (!whoAmI) {
        throw new Error('useCurrentUser must be used within the CurrentUserGuard')
    }
    return whoAmI
}

export function useCurrentUser(): WhoAmI {
    const whoAmI = useContext(CurrentUserContext)
    if (!whoAmI) {
        throw new Error('useCurrentUser must be used within the CurrentUserGuard')
    }
    return whoAmI
}

export function useCurrentUserRole(): WhoAmITherapistUserLinking.role {
    const therapistId = useSelectedTherapistId()
    const currentUser = useCurrentUser()
    const role = currentUser.therapists.find((linking) => linking.therapist.id === therapistId)?.role
    if (!role) {
        throw new Error('Could not find selected therapist in user context')
    }
    return role
}

export function useLoginStatus(): boolean {
    const currentLoginStatus = useContext(CurrentLoginStatusContext)
    return currentLoginStatus
}

export function useSelectedTherapist() {
    const selectedTherapistId = useSelectedTherapistId()
    return useTherapistsApiRequest(['therapist', selectedTherapistId], (api, [_, therapistId]) =>
        api.therapists.getTherapist(therapistId)
    )
}

export function useSelectedTherapistId(): number {
    const selectedTherapistId = useContext(SelectedTherapistIdContext)
    if (selectedTherapistId === null) {
        throw new Error('useSelectedTherapistId must be used within the TherapistSelectionGuard')
    }
    return selectedTherapistId
}

export function useSelectedTherapistRole(): WhoAmITherapistUserLinking.role {
    const therapistId = useSelectedTherapistId()
    const currentUser = useCurrentUser()
    const role = currentUser.therapists.find((linking) => linking.therapist.id === therapistId)?.role
    if (!role) {
        throw new Error('Could not find selected therapist in user context')
    }
    return role
}

const ROLE_HIERARCHY: Record<WhoAmITherapistUserLinking.role, number> = {
    [WhoAmITherapistUserLinking.role.OWNER]: 100,
    [WhoAmITherapistUserLinking.role.ADMIN]: 80,
    [WhoAmITherapistUserLinking.role.PRACTITIONER]: 60,
    [WhoAmITherapistUserLinking.role.ASSISTANT]: 40,
    [WhoAmITherapistUserLinking.role.VIEWER]: 20,
    [WhoAmITherapistUserLinking.role.THERAPIST]: 100,
    [WhoAmITherapistUserLinking.role.USER]: 100
}

export function useHasMinimumRole(minimumRole: WhoAmITherapistUserLinking.role): boolean {
    const currentRole = useSelectedTherapistRole()
    return ROLE_HIERARCHY[currentRole] >= ROLE_HIERARCHY[minimumRole]
}

/** Checks if a role is the owner/therapist role (OWNER or legacy THERAPIST) */
export function isOwnerRole(role: string): boolean {
    return role === WhoAmITherapistUserLinking.role.OWNER || role === WhoAmITherapistUserLinking.role.THERAPIST
}

/**
 * Check if the current user is a subuser (managed by another therapist)
 * Subusers should not see payment-related features as payments are handled by their manager
 */
export function useIsSubuser(): boolean {
    const currentUser = useCurrentUser()
    return currentUser.managedByTherapist !== null
}

/**
 * Check if the current user is a managing user (multi-user manager with subusers)
 * Managing users should see aggregated data from all their subusers
 */
export function useIsManagingUser(): boolean {
    const currentUser = useCurrentUser()
    return currentUser.isMultiUserManager
}

export function useSelectedPublicTherapist() {
    const { slug } = useSelectedClientWhoAmITherapist()
    return usePublicApiRequest(['public-therapist', slug], (api, [_, slug]) => api.therapists.getPublicTherapist(slug))
}

export function useSelectedClientTherapist() {
    const therapistId = useSelectedTherapistId()
    const {
        data: therapist,
        showLoading,
        error,
        mutate
    } = useClientsApiRequest(['therapist', therapistId], (api) => api.therapists.getTherapist(therapistId))
    return { therapist, showLoading, error, mutate }
}

export function useSelectedClientTherapistFromWhoAmI(): MinimalWhoAmITherapist | undefined {
    const currentClient = useCurrentClient()
    const selectedTherapistId = useSelectedTherapistId()
    return useMemo(
        () => currentClient.therapists.find((therapist) => therapist.id === selectedTherapistId),
        [currentClient.therapists, selectedTherapistId]
    )
}

export function useSelectedClientWhoAmITherapist() {
    const selectedTherapistId = useSelectedTherapistId()
    const currentClient = useCurrentClient()
    const selectedTherapist = currentClient.therapists.find((therapist) => therapist.id === selectedTherapistId)
    if (!selectedTherapist) {
        throw new Error('Could not find selected therapist in client context')
    }
    return selectedTherapist
}

export function useSelectedUserTherapistFromWhoAmI(): WhoAmITherapist {
    const currentUser = useCurrentUser()
    const selectedTherapistId = useSelectedTherapistId()
    const therapist = currentUser.therapists.find((linking) => linking.therapist.id === selectedTherapistId)?.therapist
    if (!therapist) {
        throw new Error('Could not find selected therapist in user context')
    }
    return therapist
}

export function useTherapistContract(): Contract | null {
    const therapist = useSelectedUserTherapistFromWhoAmI()
    return therapist.contract || null
}

export function useTherapistPlan(): Contract.plan | null {
    const contract = useTherapistContract()
    return contract?.plan || null
}

export function isPaidTierPlan(plan: Contract.plan | MinimalWhoAmITherapist.plan | string | null | undefined): boolean {
    return plan === 'paid' || plan === 'multiUser'
}

export function useIsPaidPlan(): boolean {
    const plan = useTherapistPlan()
    return isPaidTierPlan(plan)
}

export function useCategory(categoryId: number): EventCategory | undefined {
    const therapistId = useSelectedTherapistId()
    const { data: categories } = useTherapistsApiRequest(['eventCategories', therapistId], (api) =>
        api.eventCategories.getAllEventCategories(therapistId)
    )
    return categories?.find((category) => category.id === categoryId)
}

export function useRule(ruleId: number): EventRule | undefined {
    const therapistId = useSelectedTherapistId()
    const { data: rules } = useTherapistsApiRequest(['rules', therapistId], (api) =>
        api.eventRules.getAllEventRules(therapistId)
    )
    return rules?.find((rule) => rule.id === ruleId)
}

export function useCategoryMapping() {
    const therapistId = useSelectedTherapistId()
    const [categoryMapping, setCategoryMapping] = useState<{ [key: number]: EventCategory }>({})
    const {
        data: categories,
        showLoading,
        error,
        mutate
    } = useClientsApiRequest(['eventCategories', therapistId], (api) =>
        api.eventCategories.getAllEventCategoriesForClients(therapistId)
    )

    useEffect(() => {
        if (categories !== undefined) {
            setCategoryMapping(
                categories.reduce(
                    (acc, category) => {
                        acc[category.id] = category
                        return acc
                    },
                    {} as { [key: number]: EventCategory }
                )
            )
        }
    }, [categories])

    return { data: categoryMapping, showLoading, error, mutate }
}

// API Queries

function useApiRequest<
    API extends typeof PublicTebutoAPI | typeof TherapistsTebutoAPI | typeof ClientsTebutoAPI,
    Data,
    SWRKey extends Key = Key,
    SWROptions extends SWRConfiguration<Data, Error, BareFetcher<Data>> | undefined =
        | SWRConfiguration<Data, Error, BareFetcher<Data>>
        | undefined
>(
    api: API,
    key: SWRKey,
    request: (api: API, key: SWRKey) => FetcherResponse<Data>,
    config?: SWROptions & AdditionalRequestConfig
): SWRResponse<Data> & { showLoading: boolean } {
    const [isSlowLoading, setIsSlowLoading] = useState(false)

    const response = useSWR(key, (key: SWRKey) => request(api, key), {
        onError: defaultErrorHandler,
        onLoadingSlow: () => setIsSlowLoading(true),
        onSuccess: () => setIsSlowLoading(false),
        ...config
    })

    // Reset slow loading state when data arrives or error occurs
    useEffect(() => {
        if (response.data !== undefined || response.error) {
            setIsSlowLoading(false)
        }
    }, [response.data, response.error])

    // Only show loading if:
    // 1. The request is taking a long time (onLoadingSlow fired)
    // 2. There's no cached data available
    // 3. There's no error
    // 4. SWR is currently validating (actively fetching)
    return {
        ...response,
        showLoading: isSlowLoading && response.data === undefined && !response.error && response.isValidating
    }
}

export function usePublicApiRequest<
    Data,
    SWRKey extends Key = Key,
    SWROptions extends SWRConfiguration<Data, Error, BareFetcher<Data>> | undefined =
        | SWRConfiguration<Data, Error, BareFetcher<Data>>
        | undefined
>(
    key: SWRKey,
    request: (api: typeof PublicTebutoAPI, key: SWRKey) => FetcherResponse<Data>,
    config?: SWROptions & AdditionalRequestConfig
): SWRResponse<Data> & { showLoading: boolean } {
    return useApiRequest(PublicTebutoAPI, key, request, config)
}

export function useTherapistsApiRequest<
    Data,
    SWRKey extends Key = Key,
    SWROptions extends SWRConfiguration<Data, Error, BareFetcher<Data>> | undefined =
        | SWRConfiguration<Data, Error, BareFetcher<Data>>
        | undefined
>(
    key: SWRKey,
    request: (api: typeof TherapistsTebutoAPI, key: SWRKey) => FetcherResponse<Data>,
    config?: SWROptions & AdditionalRequestConfig
): SWRResponse<Data> & { showLoading: boolean } {
    return useApiRequest(TherapistsTebutoAPI, key, request, config)
}

export function useClientsApiRequest<
    Data,
    SWRKey extends Key = Key,
    SWROptions extends SWRConfiguration<Data, Error, BareFetcher<Data>> | undefined =
        | SWRConfiguration<Data, Error, BareFetcher<Data>>
        | undefined
>(
    key: SWRKey,
    request: (api: typeof ClientsTebutoAPI, key: SWRKey) => FetcherResponse<Data>,
    config?: SWROptions & AdditionalRequestConfig
): SWRResponse<Data> & { showLoading: boolean } {
    return useApiRequest(ClientsTebutoAPI, key, request, config)
}

// API Mutations

type TebutoApiMutationConfiguration<Data, ExtraArg, SWRMutationKey extends Key = Key> = Omit<
    SWRMutationConfiguration<Data, Error, SWRMutationKey, ExtraArg, Data>,
    'onSuccess'
> &
    AdditionalMutationRequestConfig & {
        onSuccess?: (
            data: Data,
            key: SWRMutationKey,
            config: Readonly<SWRMutationConfiguration<Data, Error, SWRMutationKey, ExtraArg, Data>> &
                (ExtraArg extends never ? object : { arg: ExtraArg })
        ) => void
    }

function useApiMutation<
    API extends typeof PublicTebutoAPI | typeof TherapistsTebutoAPI | typeof ClientsTebutoAPI,
    Data,
    ExtraArg = never,
    SWRMutationKey extends Key = Key
>(
    api: API,
    key: SWRMutationKey,
    request: (api: API, options: ExtraArg, key: SWRMutationKey) => FetcherResponse<Data>,
    config?: TebutoApiMutationConfiguration<Data, ExtraArg, SWRMutationKey>
): SWRMutationResponse<Data, any, SWRMutationKey, ExtraArg> {
    const { mutate } = useSWRConfig()
    const mutationArgRef = useRef<ExtraArg | undefined>(undefined)

    return useSWRMutation(
        key,
        ((swrKey: SWRMutationKey, options: { arg: ExtraArg }) => {
            mutationArgRef.current = options.arg
            return request(api, options.arg, swrKey)
        }) as MutationFetcher<Data, SWRMutationKey, ExtraArg>,
        {
            onError: defaultErrorHandler,
            ...config,
            onSuccess: (data, swrKey, responseConfig) => {
                const arg = mutationArgRef.current
                const responseConfigWithArg = arg === undefined ? responseConfig : { ...responseConfig, arg }

                if (config?.onSuccess) {
                    config.onSuccess(
                        data,
                        swrKey as SWRMutationKey,
                        responseConfigWithArg as Readonly<
                            SWRMutationConfiguration<Data, Error, SWRMutationKey, ExtraArg, Data>
                        > &
                            (ExtraArg extends never ? object : { arg: ExtraArg })
                    )
                } else {
                    mutate(swrKey, data, responseConfig)
                }
                if (config?.successMessage !== null) {
                    toast.success(config?.successMessage || 'Gespeichert')
                }
            }
        }
    )
}

export function usePublicApiMutation<Data, ExtraArg = never, SWRMutationKey extends Key = Key>(
    key: SWRMutationKey,
    request: (api: typeof PublicTebutoAPI, option: ExtraArg, key: SWRMutationKey) => FetcherResponse<Data>,
    config?: TebutoApiMutationConfiguration<Data, ExtraArg, SWRMutationKey>
): SWRMutationResponse<Data, any, SWRMutationKey, ExtraArg> {
    return useApiMutation(PublicTebutoAPI, key, request, config)
}

export function useTherapistsApiMutation<Data, ExtraArg = never, SWRMutationKey extends Key = Key>(
    key: SWRMutationKey,
    request: (api: typeof TherapistsTebutoAPI, options: ExtraArg, key: SWRMutationKey) => FetcherResponse<Data>,
    config?: TebutoApiMutationConfiguration<Data, ExtraArg, SWRMutationKey>
): SWRMutationResponse<Data, any, SWRMutationKey, ExtraArg> {
    return useApiMutation(TherapistsTebutoAPI, key, request, config)
}

export function useClientsApiMutation<Data, ExtraArg = never, SWRMutationKey extends Key = Key>(
    key: SWRMutationKey,
    request: (api: typeof ClientsTebutoAPI, options: ExtraArg, key: SWRMutationKey) => FetcherResponse<Data>,
    config?: TebutoApiMutationConfiguration<Data, ExtraArg, SWRMutationKey>
): SWRMutationResponse<Data, any, SWRMutationKey, ExtraArg> {
    return useApiMutation(ClientsTebutoAPI, key, request, config)
}

export function defaultErrorHandler(err: any, _key: string, config: any) {
    if (config.handleUnauthorized !== false) {
        if (err instanceof TherapistsApiError && err.status === 401) {
            window.location.href = `${process.env.NEXT_PUBLIC_THERAPISTS_AUTHORIZE_URL}?returnTo=${window.location.href}`
            return
        }

        if (err instanceof ClientsApiError && err.status === 401) {
            window.location.href = '/login'
            return
        }
    }

    let errorMessage = ERROR_MESSAGES.Unknown
    if (err instanceof TherapistsApiError || err instanceof ClientsApiError || err instanceof PublicApiError) {
        errorMessage = resolveErrorMessage(err.body)
    }

    toast.error(errorMessage, { toastId: errorMessage })
}

interface AdditionalRequestConfig {
    handleUnauthorized?: boolean
}

interface AdditionalMutationRequestConfig extends AdditionalRequestConfig {
    successMessage?: string | null
}

export function useComponentIsVisible(elementRef: MutableRefObject<HTMLElement | null>, threshold = 0.2): boolean {
    const [isVisible, setIsVisible] = useState(false)

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting)
            },
            {
                root: null, // viewport
                rootMargin: '0px', // no margin
                threshold // % of target visible
            }
        )

        if (elementRef.current) {
            observer.observe(elementRef.current)
        }

        // Clean up the observer
        return () => {
            if (elementRef.current) {
                observer.unobserve(elementRef.current)
            }
        }
    }, [])

    return isVisible
}

interface UseWebSocketsProps {
    url: string
    path: string
}

export function useWebSockets({ url, path }: UseWebSocketsProps) {
    const socketRef = useRef<Socket | null>(null)
    const pendingSubscriptionsRef = useRef<Array<{ event: string; callback: (message: unknown) => void }>>([])
    const [isConnected, setIsConnected] = useState(false)

    const applyPendingSubscriptions = useCallback(() => {
        if (socketRef.current && pendingSubscriptionsRef.current.length > 0) {
            for (const { event, callback } of pendingSubscriptionsRef.current) {
                console.debug('Applying pending subscription:', event)
                socketRef.current.on(event, callback)
            }
            pendingSubscriptionsRef.current = []
        }
    }, [])

    const connect = useCallback(() => {
        if (socketRef.current) {
            return
        }

        const socket = io(url, {
            path,
            transports: ['websocket', 'polling'],
            autoConnect: true,
            withCredentials: true
        })

        socket.on('connect', () => {
            console.log('Connected to chat WebSocket')
            setIsConnected(true)
            applyPendingSubscriptions()
        })

        socket.on('disconnect', () => {
            console.log('Disconnected from chat WebSocket')
            setIsConnected(false)
        })

        socket.on('connect_error', (error) => {
            console.error('Chat WebSocket connection error:', error)
        })

        socketRef.current = socket

        // Apply any subscriptions that were registered before socket was created
        applyPendingSubscriptions()
    }, [url, path, applyPendingSubscriptions])

    const disconnect = useCallback(() => {
        if (socketRef.current) {
            socketRef.current.disconnect()
            socketRef.current = null
            setIsConnected(false)
        }
    }, [])

    useEffect(() => {
        connect()
        return disconnect
    }, [connect, disconnect])

    const subscribe = useCallback(<T>(event: string, callback: (message: T) => void) => {
        if (socketRef.current) {
            console.debug('Subscribing to event:', event)
            socketRef.current.on(event, callback as (message: unknown) => void)
        } else {
            // Queue subscription if socket isn't ready yet
            console.debug('Queueing subscription for event:', event)
            pendingSubscriptionsRef.current.push({ event, callback: callback as (message: unknown) => void })
        }
    }, [])

    const unsubscribe = useCallback(<T>(event: string, callback: (message: T) => void) => {
        if (socketRef.current) {
            console.debug('Unsubscribing from event:', event)
            socketRef.current.off(event, callback as (message: unknown) => void)
        } else {
            // Remove from pending subscriptions if socket isn't ready yet
            pendingSubscriptionsRef.current = pendingSubscriptionsRef.current.filter(
                (sub) => !(sub.event === event && sub.callback === callback)
            )
        }
    }, [])

    return {
        isConnected,
        subscribe,
        unsubscribe
    }
}
